import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  Store,
  Users,
  Dice5,
  CalendarClock,
  Package,
  BarChart3,
  ShieldAlert,
  CloudUpload,
  LogOut,
  Terminal,
  Network,
  Boxes,
  Signal,
  Vault,
  ReceiptText,
  ClipboardList,
} from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ConnectLogo } from "@/components/brand/ConnectLogo";
import ThemeToggle from "@/components/brand/ThemeToggle";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, testid: "admin-nav-dashboard", end: true, hideForViewer: true },
  { to: "/admin/bancas", label: "Bancas", icon: Building2, testid: "admin-nav-bancas", adminOnly: true },
  { to: "/admin/grupos", label: "Grupos", icon: Network, testid: "admin-nav-grupos", adminBanca: true },
  { to: "/admin/agencias", label: "Agencias", icon: Boxes, testid: "admin-nav-agencias", adminBanca: true },
  { to: "/admin/taquillas", label: "Taquillas", icon: Store, testid: "admin-nav-taquillas", adminBanca: true },
  { to: "/admin/vendedores", label: "Vendedores", icon: Users, testid: "admin-nav-vendedores", adminBanca: true },
  { to: "/admin/loterias", label: "Loterías", icon: Dice5, testid: "admin-nav-loterias", adminOnly: true },
  { to: "/admin/sorteos", label: "Sorteos & Resultados", icon: CalendarClock, testid: "admin-nav-sorteos", adminOnly: true },
  { to: "/admin/cupos", label: "Cupos", icon: Package, testid: "admin-nav-cupos", adminOnly: true },
  { to: "/admin/servicios", label: "Servicios", icon: Signal, testid: "admin-nav-servicios", adminBanca: true },
  { to: "/admin/reportes", label: "Reportes", icon: BarChart3, testid: "admin-nav-reportes" },
  { to: "/admin/auditoria", label: "Auditoría", icon: ShieldAlert, testid: "admin-nav-auditoria", adminBanca: true },
  { to: "/admin/caja-00", label: "Consolidado Caja 00", icon: Vault, testid: "admin-nav-caja00", end: true, madreOnly: true },
  { to: "/admin/caja-00/tickets", label: "Historial de Tickets", icon: ReceiptText, testid: "admin-nav-caja00-tickets", madreOnly: true },
  { to: "/admin/caja-00/cuadre", label: "Reportes de Cuadre", icon: ClipboardList, testid: "admin-nav-caja00-cuadre", madreOnly: true },
  { to: "/admin/updates", label: "Actualizaciones", icon: CloudUpload, testid: "admin-nav-updates", adminOnly: true },
];

export default function AdminShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const isViewer = user?.role === "grupo_owner" || user?.role === "agencia_owner";
  const isMadre = user?.role === "taquilla_madre_operator";
  const visibleNav = NAV.filter((n) => {
    if (isMadre || n.madreOnly) return isMadre && n.madreOnly;
    if (n.adminOnly && user?.role !== "admin") return false;
    if (n.adminBanca && !(user?.role === "admin" || user?.role === "banca_owner")) return false;
    if (isViewer && n.hideForViewer) return false;
    return true;
  });

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[260px_1fr] bg-background">
      <aside
        data-testid="admin-sidebar"
        className="hidden lg:flex flex-col justify-between h-screen sticky top-0 overflow-hidden border-r border-border bg-card"
      >
        <div className="flex-shrink-0 px-5 py-5 flex items-center">
          <ConnectLogo height={32} className="rounded-md" />
        </div>
        <Separator />
        <nav className="flex-1 min-h-0 overflow-y-auto pl-2 pr-1 py-3 space-y-1" data-testid="admin-sidebar-nav">
          {visibleNav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium border-l-2 ${
                    isActive
                      ? "bg-secondary text-foreground border-primary"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary/60 border-transparent"
                  }`
                }
              >
                <Icon size={18} strokeWidth={1.75} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="flex-shrink-0 border-t border-border p-3 space-y-2" data-testid="admin-sidebar-footer">
          {(user?.role === "admin") && (
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start"
              onClick={() => navigate("/pos")}
              data-testid="admin-open-pos-button"
            >
              <Terminal size={16} strokeWidth={1.75} className="mr-2" /> Abrir POS
            </Button>
          )}
          <div className="rounded-lg border border-border/70 bg-card px-3 py-2">
            <div className="text-xs text-muted-foreground">Sesión</div>
            <div className="text-sm font-medium truncate" data-testid="admin-user-name">{user?.full_name}</div>
            <div className="text-[11px] uppercase tracking-widest text-primary">{user?.role === "taquilla_madre_operator" ? "Taquilla Madre (Caja 00)" : user?.role}</div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="flex-1 justify-start text-muted-foreground hover:text-destructive"
              onClick={doLogout}
              data-testid="admin-logout-button"
            >
              <LogOut size={16} strokeWidth={1.75} className="mr-2" /> Cerrar sesión
            </Button>
            <ThemeToggle />
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-20 flex items-center justify-between px-4 py-3 border-b border-border bg-card">
        <ConnectLogo height={28} className="rounded-md" />
        <div className="flex items-center gap-1.5">
          <ThemeToggle />
          <Button variant="ghost" size="sm" onClick={doLogout} data-testid="admin-logout-button-mobile">
            <LogOut size={16} />
          </Button>
        </div>
      </div>

      <main className="min-w-0">
        <div className="lg:hidden overflow-x-auto border-b border-border bg-card px-2 py-2 flex gap-2">
          {visibleNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium border ${
                  isActive ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </div>
        <div className="px-4 sm:px-6 lg:px-8 py-5 max-w-[1400px]">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
